/**
 * EpicMergePanel - Entity-specific merge panel for Epics
 * 
 * Provides a familiar tab-based UI (Overview | KPIs | Features | Attachments) for both
 * source items (view mode) and merged result (edit mode).
 * 
 * This replaces the generic dynamic merge UI when entityType === 'epic',
 * giving users a clear understanding of what data is being kept vs lost.
 * 
 * Props:
 * - sourceItems: array of epic objects fetched from DB
 * - selectedItems: { [idx]: boolean } - which source items are included
 * - expandedItems: { [idx]: boolean } - which source items are expanded
 * - onToggleSelect: (idx) => void
 * - onToggleExpand: (idx) => void
 * - formData: merged epic form data
 * - onFormChange: (field, value) => void
 * - productId: string
 * - errors: { [field]: string } - validation errors
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { initiativesApi, personasApi } from '../../services/api';
import {
  ChevronDown,
  ChevronRight,
  Rocket,
  Target,
  BarChart3,
  Layers,
  Paperclip,
  Plus,
  Minus,
  AlertTriangle,
} from 'lucide-react';

// Import reusable form components
import EpicOverviewForm, { EPIC_STATUS_OPTIONS } from '../epic/EpicOverviewForm';
import EpicFeaturesPanel from '../epic/EpicFeaturesPanel';

// =============================================================================
// SOURCE EPIC CARD (Left Panel)
// =============================================================================

const SourceEpicCard = ({
  epic,
  index,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  initiatives,
  personas,
  colors,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const statusConfig = EPIC_STATUS_OPTIONS.find(s => s.value === epic.status) || EPIC_STATUS_OPTIONS[0];
  const initiativeName = initiatives.find(i => i.initiative_id === epic.initiative_id)?.title || 
                         epic.initiative?.title || 'Not linked';
  const featureCount = epic.features?.length || 0;
  // KPI count: use inherited_kpis if available (from /details), fallback to subscribed_kpi_ids
  const inheritedKpis = epic.inherited_kpis || [];
  const subscribedKpis = inheritedKpis.filter(k => k.is_subscribed);
  const kpiCount = subscribedKpis.length || epic.subscribed_kpi_ids?.length || 0;
  const attachmentCount = epic.attachment_count || 0;

  return (
    <div 
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        isSelected 
          ? "border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-800" 
          : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60"
      )}
      data-testid={`source-epic-${index}`}
    >
      {/* Header - Always Visible */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        )}
        onClick={onToggleExpand}
      >
        {/* Selection Checkbox */}
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="border-gray-400"
          />
        </div>
        
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        
        {/* Epic Icon */}
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          isSelected ? "bg-blue-100 dark:bg-blue-900/30" : "bg-gray-200 dark:bg-gray-700"
        )}>
          <Rocket className={cn("w-4 h-4", isSelected ? "text-blue-600" : "text-gray-500")} />
        </div>
        
        {/* Epic Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-medium truncate",
              isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 line-through"
            )}>
              {epic.title || 'Untitled Epic'}
            </span>
            {!isSelected && (
              <Badge variant="outline" className="text-xs text-red-500 border-red-300">
                Excluded
              </Badge>
            )}
          </div>
          {!isExpanded && (
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              <span className={cn("px-1.5 py-0.5 rounded", statusConfig.color, "text-white text-[10px]")}>
                {statusConfig.label}
              </span>
              <span>•</span>
              <span>{initiativeName}</span>
              {featureCount > 0 && (
                <>
                  <span>•</span>
                  <span>{featureCount} feature{featureCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded Content - Tab-based View */}
      {isExpanded && (
        <div className={cn("border-t", colors.borderPrimary)}>
          {/* Tabs */}
          <div className="px-3 pt-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gray-100 dark:bg-gray-800">
                <TabsTrigger value="overview" className="text-xs">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="kpis" className="text-xs">
                  KPIs ({kpiCount})
                </TabsTrigger>
                <TabsTrigger value="features" className="text-xs">
                  Features ({featureCount})
                </TabsTrigger>
                <TabsTrigger value="attachments" className="text-xs">
                  Files ({attachmentCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content */}
          <div className="p-3">
            {activeTab === 'overview' && (
              <EpicOverviewForm
                formData={epic}
                isEditing={false}
                isDisabled={true}
                colors={colors}
                initiatives={initiatives}
                personas={personas}
                mode="view"
              />
            )}
            
            {activeTab === 'kpis' && (
              <div className="space-y-2">
                {subscribedKpis.length > 0 ? (
                  <>
                    <p className={cn("text-xs font-medium mb-2", colors.textMuted)}>
                      Subscribed KPIs ({subscribedKpis.length})
                    </p>
                    <div className="space-y-2">
                      {subscribedKpis.map((kpi) => (
                        <div 
                          key={kpi.kpi_key}
                          className={cn("p-2 rounded-lg border text-sm", colors.bgTertiary, colors.borderPrimary)}
                        >
                          <div className="flex items-center gap-2">
                            <Target className="w-3 h-3 text-purple-500 flex-shrink-0" />
                            <span className={cn("font-medium", colors.textPrimary)}>{kpi.metric_name}</span>
                          </div>
                          <div className={cn("text-xs mt-1 flex items-center gap-2", colors.textMuted)}>
                            <span className="text-purple-400">{kpi.goal_title}</span>
                            {kpi.target_value !== undefined && (
                              <>
                                <span>•</span>
                                <span>Target: {kpi.target_value}{kpi.metric_format === 'percent' ? '%' : ''}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className={cn("text-xs mt-2 text-center", colors.textMuted)}>
                      KPI subscriptions will be combined in the merged result
                    </p>
                  </>
                ) : (
                  <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
                    <Target className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                    <p className={cn("text-sm", colors.textMuted)}>
                      No KPI targets subscribed
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'features' && (
              <EpicFeaturesPanel
                features={epic.features || []}
                isLoading={false}
                colors={colors}
                mode="view"
              />
            )}
            
            {activeTab === 'attachments' && (
              <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
                <Paperclip className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                <p className={cn("text-sm", colors.textMuted)}>
                  {attachmentCount > 0 
                    ? `${attachmentCount} file${attachmentCount !== 1 ? 's' : ''} attached`
                    : 'No attachments'}
                </p>
                <p className={cn("text-xs mt-1", colors.textMuted)}>
                  Attachments will need to be re-uploaded after merge
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MERGE DIFF SUMMARY
// =============================================================================

const MergeDiffSummary = ({ sourceItems, selectedItems, colors }) => {
  // Calculate what's being kept vs lost
  const selectedEpics = sourceItems.filter((_, idx) => selectedItems[idx]);
  const excludedEpics = sourceItems.filter((_, idx) => !selectedItems[idx]);
  
  // Count features
  const totalSelectedFeatures = selectedEpics.reduce((sum, e) => sum + (e.features?.length || 0), 0);
  const excludedFeatures = excludedEpics.reduce((sum, e) => sum + (e.features?.length || 0), 0);
  
  // Count KPIs
  const totalSelectedKpis = new Set(selectedEpics.flatMap(e => e.subscribed_kpi_ids || e.selected_targets || [])).size;
  const excludedKpis = new Set(excludedEpics.flatMap(e => e.subscribed_kpi_ids || e.selected_targets || [])).size;

  if (excludedEpics.length === 0) {
    return null; // No diff to show
  }

  return (
    <div className={cn("p-3 rounded-lg border mb-4", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700")}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
          Merge Impact Summary
        </span>
      </div>
      <div className="space-y-1 text-xs">
        {excludedEpics.length > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedEpics.length} epic(s) excluded: {excludedEpics.map(e => `"${e.title}"`).join(', ')}
            </span>
          </div>
        )}
        {excludedFeatures > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedFeatures} feature(s) from excluded epics will be orphaned
            </span>
          </div>
        )}
        {excludedKpis > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              KPI subscriptions from excluded epics will be lost
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t border-amber-300 dark:border-amber-600 mt-2">
          <Plus className="w-3 h-3 text-green-500" />
          <span className="text-green-700 dark:text-green-300">
            Merged epic will have {totalSelectedFeatures} feature(s) from {selectedEpics.length} selected epic(s)
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MERGED EPIC FORM (Right Panel)
// =============================================================================

const MergedEpicForm = ({
  formData,
  onFormChange,
  initiatives,
  personas,
  colors,
  errors = {},
  sourceItems = [],
  selectedItems = {},
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const handleChange = (field, value) => {
    onFormChange(field, value);
  };

  // Calculate totals from selected source items
  const selectedEpics = sourceItems.filter((_, idx) => selectedItems[idx]);
  
  // Combine features from all selected epics
  const allFeatures = selectedEpics.flatMap(e => e.features || []);
  const featureCount = allFeatures.length;
  
  // Combine unique KPI subscriptions from all selected epics
  const allKpiKeys = new Set();
  const allKpis = [];
  selectedEpics.forEach(epic => {
    const subscribedKpis = (epic.inherited_kpis || []).filter(k => k.is_subscribed);
    subscribedKpis.forEach(kpi => {
      if (!allKpiKeys.has(kpi.kpi_key)) {
        allKpiKeys.add(kpi.kpi_key);
        allKpis.push(kpi);
      }
    });
  });
  const kpiCount = allKpis.length;

  // Check if there are errors in overview fields
  const hasOverviewErrors = Object.keys(errors).some(k => ['title', 'description', 'problem_statement'].includes(k));

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="flex-shrink-0 pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn("bg-gray-100 dark:bg-gray-800")}>
            <TabsTrigger 
              value="overview" 
              className={cn("text-sm", hasOverviewErrors && "text-red-500")}
            >
              Overview {hasOverviewErrors && '⚠️'}
            </TabsTrigger>
            <TabsTrigger value="kpis" className="text-sm">
              KPIs ({kpiCount})
            </TabsTrigger>
            <TabsTrigger value="features" className="text-sm">
              Features ({featureCount})
            </TabsTrigger>
            <TabsTrigger value="attachments" className="text-sm">
              Attachments
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <EpicOverviewForm
            formData={formData}
            onChange={handleChange}
            isEditing={true}
            isDisabled={false}
            colors={colors}
            initiatives={initiatives}
            personas={personas}
            errors={errors}
            mode="create"
          />
        )}
        
        {activeTab === 'kpis' && (
          <div className="space-y-3">
            {allKpis.length > 0 ? (
              <>
                <p className={cn("text-xs font-medium", colors.textMuted)}>
                  Combined KPI subscriptions from {selectedEpics.length} selected epic{selectedEpics.length !== 1 ? 's' : ''}
                </p>
                <div className="space-y-2">
                  {allKpis.map((kpi) => (
                    <div 
                      key={kpi.kpi_key}
                      className={cn("p-2 rounded-lg border text-sm", colors.bgTertiary, colors.borderPrimary)}
                    >
                      <div className="flex items-center gap-2">
                        <Target className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span className={cn("font-medium", colors.textPrimary)}>{kpi.metric_name}</span>
                      </div>
                      <div className={cn("text-xs mt-1", colors.textMuted)}>
                        <span className="text-green-400">{kpi.goal_title}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className={cn("text-xs text-center pt-2", colors.textMuted)}>
                  You can adjust subscriptions after the merge is complete.
                </p>
              </>
            ) : (
              <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
                <Target className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                <p className={cn("text-sm", colors.textMuted)}>
                  No KPI subscriptions to merge
                </p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'features' && (
          <div className="space-y-3">
            {allFeatures.length > 0 ? (
              <>
                <p className={cn("text-xs font-medium", colors.textMuted)}>
                  Features from {selectedEpics.length} selected epic{selectedEpics.length !== 1 ? 's' : ''} will be re-linked
                </p>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {allFeatures.map((feature) => (
                    <div 
                      key={feature.feature_id}
                      className={cn("p-2 rounded-lg border text-sm", colors.bgTertiary, colors.borderPrimary)}
                    >
                      <div className="flex items-center gap-2">
                        <Layers className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        <span className={cn("font-medium truncate", colors.textPrimary)}>{feature.title}</span>
                        <Badge variant="outline" className={cn("ml-auto text-xs",
                          feature.status === 'Done' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                          feature.status === 'In Progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        )}>
                          {feature.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
                <p className={cn("text-xs text-center pt-2", colors.textMuted)}>
                  Feature details and requirements will be preserved.
                </p>
              </>
            ) : (
              <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
                <Layers className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                <p className={cn("text-sm", colors.textMuted)}>
                  No features to merge
                </p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'attachments' && (
          <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
            <Paperclip className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
            <p className={cn("text-sm font-medium", colors.textPrimary)}>
              Attachments cannot be merged
            </p>
            <p className={cn("text-xs mt-2", colors.textMuted)}>
              You'll need to re-upload any necessary files to the merged epic after creation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const EpicMergePanel = ({
  sourceItems,
  selectedItems,
  expandedItems,
  onToggleSelect,
  onToggleExpand,
  formData,
  onFormChange,
  productId,
  errors = {},
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Initiatives for dropdown
  const [initiatives, setInitiatives] = useState([]);
  const [loadingInitiatives, setLoadingInitiatives] = useState(true);
  
  // Personas for selection
  const [personas, setPersonas] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(true);
  
  // Load initiatives
  useEffect(() => {
    const loadInitiatives = async () => {
      if (!productId) return;
      try {
        const res = await initiativesApi.list(productId);
        setInitiatives(res.data || []);
      } catch (err) {
        console.error('Failed to load initiatives:', err);
      } finally {
        setLoadingInitiatives(false);
      }
    };
    loadInitiatives();
  }, [productId]);
  
  // Load personas
  useEffect(() => {
    const loadPersonas = async () => {
      if (!productId) return;
      try {
        const res = await personasApi.list(productId);
        setPersonas(res.data || []);
      } catch (err) {
        console.error('Failed to load personas:', err);
      } finally {
        setLoadingPersonas(false);
      }
    };
    loadPersonas();
  }, [productId]);
  
  // Selected count
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  return (
    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
      {/* Left Panel: Source Epics */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Source Epics
            </h3>
            <Badge variant="outline">
              {selectedCount} of {sourceItems.length} selected
            </Badge>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select epics to include in the merge. Expand to review data.
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {sourceItems.map((epic, idx) => (
            <SourceEpicCard
              key={epic.epic_id || idx}
              epic={epic}
              index={idx}
              isSelected={selectedItems[idx]}
              isExpanded={expandedItems[idx]}
              onToggleSelect={() => onToggleSelect(idx)}
              onToggleExpand={() => onToggleExpand(idx)}
              initiatives={initiatives}
              personas={personas}
              colors={colors}
            />
          ))}
        </div>
      </div>
      
      {/* Right Panel: Merged Epic Form */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Merged Result
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Edit the combined epic before applying
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Diff Summary */}
          <MergeDiffSummary
            sourceItems={sourceItems}
            selectedItems={selectedItems}
            colors={colors}
          />
          
          {/* Merged Form */}
          <MergedEpicForm
            formData={formData}
            onFormChange={onFormChange}
            initiatives={initiatives}
            personas={personas}
            colors={colors}
            errors={errors}
            sourceItems={sourceItems}
            selectedItems={selectedItems}
          />
        </div>
      </div>
    </div>
  );
};

export default EpicMergePanel;
